'use client'

import { useState, useEffect, useContext } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import NextLink from 'next/link'
import Image from 'next/image'
import { RootContext } from '@/modules/contextService'
import Button from '@/components/inputs/Button'
import { PATHWAY } from '@/modules/httpService'
import { IUser } from '@/interfaces/IUser'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import { useTranslations } from 'next-intl'
// import Box from '@mui/material/Box'
import Avatar from '@mui/material/Avatar'
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import type { Theme } from '@mui/material/styles'
// import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
// import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import AppsIcon from '@mui/icons-material/Apps'
// import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded'
// import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined'
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined'

import KeyIcon from '@mui/icons-material/Key'
// import Settings from '@mui/icons-material/Settings'
import Logout from '@mui/icons-material/Logout'
import styles from './PublisherHeader.module.scss'
import NextImage from 'next/image'
import imageService from '@/modules/imageService/client'
import ListSubheader from '@mui/material/ListSubheader'
import GroupIcon from '@mui/icons-material/Group'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'
import SendIcon from '@mui/icons-material/Send'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import AnalyticsIcon from '@mui/icons-material/Analytics'
import FaceRetouchingOffIcon from '@mui/icons-material/FaceRetouchingOff'
import ShowChartIcon from '@mui/icons-material/ShowChart'
// import PolicyIcon from '@mui/icons-material/Policy'
// import PrivacyTipIcon from '@mui/icons-material/PrivacyTip'
// import GavelIcon from '@mui/icons-material/Gavel'
// import ChurchIcon from '@mui/icons-material/Church'
// AdminPanelSettingsIcon removed — replaced by per-page admin links
import TranslateIcon from '@mui/icons-material/Translate'
import CloseIcon from '@mui/icons-material/Close'
import MenuIcon from '@mui/icons-material/Menu'
import BedtimeOutlinedIcon from '@mui/icons-material/BedtimeOutlined'
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined'

import LanguageMenu from './LanguageMenu'
import LanguageItem from './LanguageItem'
import NightModeSelect from './NightModeSelect'
import cookieService from '@/modules/cookieService'
import ThemeClient from '@/app/v1/ThemeClient'
import NightTimeModeButton from './NightTimeModeButton'

interface PublisherHeaderProps {
  user: IUser | null | undefined
  size: 'small' | 'large' | 'xlarge'
}

function PublisherHeader ({ user, size = 'small' }: PublisherHeaderProps) {
  const { rootData, setRootData } = useContext(RootContext)
  const pathname = usePathname()
  const router = useRouter()
  const [search, setSearch] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedTheme, setSelectedTheme] = useState('')

  async function createDraft () {
    try {
      const res = await PATHWAY.post('/posts', {
        title: 'Draft',
        subtitle: null,
        content: null
      })
      const post = res.data.data.posts[0]
      router.push(`/drafts/${post.id}`)
    } catch (err) {
      console.error(err)
    }
  }

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const [drawerOpen, setDrawerOpen] = useState(false)

  function toggleNightMode () {
    const targetTheme = selectedTheme === 'light' ? 'dark' : 'light'
    document.documentElement.setAttribute('data-theme', targetTheme)
    cookieService.setCookie('theme', targetTheme)
    setSelectedTheme(targetTheme)

    setRootData({
      ...rootData,
      theme: targetTheme
    })
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  useEffect(() => {
    const storedTheme = cookieService.getCookie('theme')
    setSelectedTheme(storedTheme || 'dark')
  }, [])

  const dto = { pathname }
  const dtoString = btoa(JSON.stringify(dto))
  const t = useTranslations('header')
  const iconColor = selectedTheme === 'light' ? 'dark' : 'light'

  let wrapperSize = ''
  switch (size) {
  case 'xlarge': {
    wrapperSize = styles.innerWrapperXLarge
    break
  }
  case 'large': {
    wrapperSize = styles.innerWrapperBig
    break
  }
  case 'small': {
    wrapperSize = styles.innerWrapperSmall
    break
  }
  }

  return (
    <ThemeClient theme={selectedTheme}>
      <div className={styles.outerWrapper}>
        <div className={`${styles.innerWrapper} ${wrapperSize}`}>
          <div className={styles.leftSection}>
            <div className={styles.logo}>
              <NextLink scroll href='/feed'>
                <Image
                  src={`/img/sslm-${iconColor}-icon.svg`}
                  alt="Stepping Stones International Ministry Logo"
                  width={50}
                  height={50}
                  priority
                />
              </NextLink>
            </div>

            <div className={styles.mobileHide}>
              <Tooltip title={t('topic-menu')}>
                <NextLink href='/resources'>
                  <Button
                    value={t('topic-menu')}
                    color='primary'
                    variant='text'
                    style={{ color: 'var(--primary-font-rgb)' }}
                  />
                </NextLink>
              </Tooltip>
            </div>
          </div>

          <form
            className={styles.search}
            onSubmit={(e) => {
              e.preventDefault()
              if (search) {
                router.push(`/search?q=${encodeURIComponent(search.trim())}`)
              }
            }}
          >
            <NextImage
              src={`/img/sslm-${iconColor}-icon.svg`}
              alt=""
              width={20}
              height={20}
              className={styles.searchIcon}
            />
            <input
              type="text"
              placeholder="Search"
              value={search}
              maxLength={50}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className={styles.closeIcon}>
              {search &&
                <CloseOutlinedIcon onClick={() => setSearch('')} style={{ cursor: 'pointer' }} />
              }
              {!search &&
                <>&nbsp;&nbsp;&nbsp;</>
              }
            </div>
          </form>

          <div className={styles.rightSection}>
            <div className={styles.hamburger}>
              <IconButton onClick={() => setDrawerOpen(true)} size="medium">
                <MenuIcon style={{ color: 'var(--primary-font-rgb)' }} />
              </IconButton>
            </div>
            <div className={`${styles.buttonBar} ${user === null ? styles.buttonBarLoggedOut : ''}`}>
              {user && user.writingEnabled &&
                <Button
                  value={t('write')}
                  color='primary'
                  // gutterRight
                  variant='text'
                  startIcon={<StickyNote2OutlinedIcon />}
                  style={{
                    color: 'var(--primary-font-rgb)'
                  }}
                  onClick={async () => createDraft()}
                />
              }
              {user && !user.writingEnabled &&
                <Tooltip title={t('topic-menu')}>
                  <NextLink href='/topics'>
                    <IconButton
                      size="large"
                    >
                      <AppsIcon fontSize='large' />
                    </IconButton>
                  </NextLink>
                </Tooltip>
              }
              {user === null &&
                <Button
                  value={t('log-in')}
                  color='primary'
                  gutterRight
                  variant='outlined'
                  linkType='internal'
                  link={`/v1/users/login?dto=${dtoString}`}
                />
              }
              {user === null &&
                <Button
                  value={t('sign-up')}
                  color='secondary'
                  gutterRight
                  variant='text'
                  linkType='internal'
                  link='/v1/users/signup'
                  glitter
                />
              }
              {user === null &&
                <span className={styles.mobileHide}>
                  <NightTimeModeButton
                    selectedTheme={selectedTheme || 'dark'}
                    toggleNightMode={toggleNightMode}
                  />
                </span>
              }
              {user &&
                <Tooltip title={t('account')}>
                  <IconButton
                    onClick={handleClick}
                    size="small"
                    sx={{ ml: 2 }}
                    aria-controls={open ? 'account-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                  >
                    {user.avatar &&
                      <NextImage
                        src={imageService.postImageURL('avatars', user.avatar)}
                        alt={t('avatar')}
                        width={46}
                        height={46}
                        style={{ borderRadius: '50%', border: '1px solid rgba(0, 0, 0, 0.15)' }}
                      />
                    }
                    {!user.avatar &&
                      <Avatar sx={{ width: 46, height: 46 }}>
                        {user.username ? user.username[0].toUpperCase() : '?'}
                      </Avatar>
                    }
                  </IconButton>
                </Tooltip>
              }
              <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                PaperProps={{
                  elevation: 0,
                  // backgroundColor: 'var(--primary-chip-bg)',
                  // background: 'var(--primary-chip-bg)',
                  sx: {
                    // backgroundColor: 'var(--primary-chip-bg)',
                    // background: 'var(--primary-chip-bg)',
                    // color: 'var(--primary-font-rgb)',
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1
                    },
                    '&::before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0
                      // backgroundColor: 'none'
                    }
                  }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <NextLink href={`/${user?.username}/profile`}>
                  <MenuItem onClick={handleClose}>
                    {user && user.avatar &&
                      <NextImage
                        src={imageService.postImageURL('avatars', user.avatar)}
                        alt={t('avatar')}
                        width={23}
                        height={23}
                        style={{ borderRadius: '50%', marginRight: 12, border: '1px solid rgba(0, 0, 0, 0.15)' }}
                      />
                    }
                    {user && !user.avatar &&
                      <Avatar />
                    }
                    {user && user.username ? user.username : t('profile')}
                  </MenuItem>
                </NextLink>
                <MenuItem
                  onClick={() => {
                    handleClose()
                    setDialogOpen(true)
                  }}
                >
                  <ListItemIcon>
                    <TranslateIcon fontSize="small" />
                  </ListItemIcon>
                  {t('choose-language')}
                </MenuItem>
                <Divider style={{ margin: '0.5rem 0' }} />
                {user && user.writingEnabled &&
                  <NextLink href='/self/drafts'>
                    <MenuItem
                      onClick={handleClose}
                    >
                      <ListItemIcon>
                        <ManageAccountsOutlinedIcon fontSize="small" />
                      </ListItemIcon>
                      {t('my-account')}
                    </MenuItem>
                  </NextLink>
                }
                {user && (user.level || 0) >= 3 &&
                  <NextLink href={'/self/translations'}>
                    <MenuItem
                      onClick={handleClose}
                    >
                      <ListItemIcon>
                        <TranslateIcon fontSize="small" />
                      </ListItemIcon>
                      {t('translations')}
                    </MenuItem>
                  </NextLink>
                }
                {user && (user.level || 0) >= 3 && <>
                  <Divider style={{ margin: '0.5rem 0' }} />
                  <ListSubheader style={{ lineHeight: '2rem', fontSize: '0.75rem', background: 'transparent' }}>
                    Admin
                  </ListSubheader>
                  <NextLink href='/admin/users'><MenuItem onClick={handleClose}><ListItemIcon><GroupIcon fontSize="small" /></ListItemIcon>Users</MenuItem></NextLink>
                  <NextLink href='/admin/read-history'><MenuItem onClick={handleClose}><ListItemIcon><BookmarkBorderIcon fontSize="small" /></ListItemIcon>Read History</MenuItem></NextLink>
                  <NextLink href='/admin/feedback'><MenuItem onClick={handleClose}><ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>Feedback</MenuItem></NextLink>
                  <NextLink href='/admin/likes'><MenuItem onClick={handleClose}><ListItemIcon><ThumbUpAltIcon fontSize="small" /></ListItemIcon>Likes</MenuItem></NextLink>
                  <NextLink href='/admin/shares'><MenuItem onClick={handleClose}><ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>Shares</MenuItem></NextLink>
                  <NextLink href='/admin/referrer-analysis'><MenuItem onClick={handleClose}><ListItemIcon><AssessmentOutlinedIcon fontSize="small" /></ListItemIcon>Referrer Analysis</MenuItem></NextLink>
                  <NextLink href='/admin/cohort-analysis'><MenuItem onClick={handleClose}><ListItemIcon><AnalyticsIcon fontSize="small" /></ListItemIcon>Cohort Analysis</MenuItem></NextLink>
                  <NextLink href='/admin/suspensions'><MenuItem onClick={handleClose}><ListItemIcon><FaceRetouchingOffIcon fontSize="small" /></ListItemIcon>Suspensions</MenuItem></NextLink>
                  <NextLink href='/admin/traffic'><MenuItem onClick={handleClose}><ListItemIcon><ShowChartIcon fontSize="small" /></ListItemIcon>Traffic</MenuItem></NextLink>
                </>}
                <NextLink href={`/self/confirm?dto=${btoa(JSON.stringify({ pathname: '/self/update-password' }))}`}>
                  <MenuItem
                    onClick={handleClose}
                  >
                    <ListItemIcon>
                      <KeyIcon fontSize="small" />
                    </ListItemIcon>
                    {t('change-password')}
                  </MenuItem>
                </NextLink>
                <Divider style={{ margin: '0.5rem 0' }} />
                <NightModeSelect
                  selectedTheme={selectedTheme || 'dark'}
                  toggleNightMode={toggleNightMode}
                />
                <Divider style={{ margin: '0.5rem 0' }} />
                {/* <ListSubheader
                  style={{ background: 'var(--primary-chip-bg)', color: 'var(--primary-font-rgb)' }}
                >
                  {t('policies')}
                </ListSubheader> */}
                {/* <NextLink href={'/policies/terms-of-service'}>
                  <MenuItem
                    onClick={handleClose}
                  >
                    <ListItemIcon>
                      <PolicyIcon fontSize="small" />
                    </ListItemIcon>
                    {t('terms-of-service')}
                  </MenuItem>
                </NextLink> */}
                {/* <NextLink href={'/policies/privacy-policy'}>
                  <MenuItem
                    onClick={handleClose}
                  >
                    <ListItemIcon>
                      <PrivacyTipIcon fontSize="small" />
                    </ListItemIcon>
                    {t('privacy-policy')}
                  </MenuItem>
                </NextLink> */}
                {/* <NextLink href={'/policies/community-guidelines'}>
                  <MenuItem
                    onClick={handleClose}
                  >
                    <ListItemIcon>
                      <GavelIcon fontSize="small" />
                    </ListItemIcon>
                    {t('community-guidelines')}
                  </MenuItem>
                </NextLink> */}
                {/* <NextLink href={'/policies/statement-of-faith'}>
                  <MenuItem
                    onClick={handleClose}
                  >
                    <ListItemIcon>
                      <ChurchIcon fontSize="small" />
                    </ListItemIcon>
                    {t('statement-of-faith')}
                  </MenuItem>
                </NextLink> */}
                {/* <Divider style={{ margin: '0.5rem 0' }} /> */}
                <NextLink href='/v1/logout'>
                  <MenuItem onClick={handleClose}>
                    <ListItemIcon>
                      <Logout fontSize="small" />
                    </ListItemIcon>
                    {t('logout')}
                  </MenuItem>
                </NextLink>
              </Menu>
            </div>
          </div>
        </div>
      </div>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 280, background: 'var(--background-color)', color: 'var(--primary-font-rgb)' } }}
      >
        <div className={styles.drawerHeader}>
          <IconButton onClick={() => setDrawerOpen(false)}>
            <CloseIcon style={{ color: 'var(--primary-font-rgb)' }} />
          </IconButton>
        </div>
        <List disablePadding>
          <NextLink href='/search' onClick={() => setDrawerOpen(false)}>
            <ListItem disablePadding>
              <ListItemButton>
                <ListItemIcon><SearchOutlinedIcon style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>
                {t('search')}
              </ListItemButton>
            </ListItem>
          </NextLink>
          <NextLink href='/resources' onClick={() => setDrawerOpen(false)}>
            <ListItem disablePadding>
              <ListItemButton>
                <ListItemIcon><TravelExploreOutlinedIcon style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>
                {t('topic-menu')}
              </ListItemButton>
            </ListItem>
          </NextLink>
          {user && user.writingEnabled && (
            <ListItem disablePadding>
              <ListItemButton onClick={() => { setDrawerOpen(false); createDraft() }}>
                <ListItemIcon><StickyNote2OutlinedIcon style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>
                {t('write')}
              </ListItemButton>
            </ListItem>
          )}
          {user && (
            <NextLink href={`/${user.username}/profile`} onClick={() => setDrawerOpen(false)}>
              <ListItem disablePadding>
                <ListItemButton>
                  <ListItemIcon>
                    {user.avatar
                      ? <NextImage src={imageService.postImageURL('avatars', user.avatar)} alt={t('avatar')} width={24} height={24} style={{ borderRadius: '50%' }} />
                      : <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>{user.username?.[0].toUpperCase()}</Avatar>
                    }
                  </ListItemIcon>
                  {user.username}
                </ListItemButton>
              </ListItem>
            </NextLink>
          )}
          {user && user.writingEnabled && (
            <NextLink href='/self/drafts' onClick={() => setDrawerOpen(false)}>
              <ListItem disablePadding>
                <ListItemButton>
                  <ListItemIcon><ManageAccountsOutlinedIcon style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>
                  {t('my-account')}
                </ListItemButton>
              </ListItem>
            </NextLink>
          )}
          {user && (user.level || 0) >= 3 && (
            <NextLink href='/self/translations' onClick={() => setDrawerOpen(false)}>
              <ListItem disablePadding>
                <ListItemButton>
                  <ListItemIcon><TranslateIcon style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>
                  {t('translations')}
                </ListItemButton>
              </ListItem>
            </NextLink>
          )}
          {user && (user.level || 0) >= 3 && (<>
            <Divider style={{ margin: '0.5rem 0' }} />
            <ListSubheader style={{ background: 'transparent', color: 'var(--secondary-font-rgb)', fontSize: '0.75rem', lineHeight: '2rem' }}>
              Admin
            </ListSubheader>
            <NextLink href='/admin/users' onClick={() => setDrawerOpen(false)}>
              <ListItem disablePadding><ListItemButton><ListItemIcon><GroupIcon style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>Users</ListItemButton></ListItem>
            </NextLink>
            <NextLink href='/admin/read-history' onClick={() => setDrawerOpen(false)}>
              <ListItem disablePadding><ListItemButton><ListItemIcon><BookmarkBorderIcon style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>Read History</ListItemButton></ListItem>
            </NextLink>
            <NextLink href='/admin/feedback' onClick={() => setDrawerOpen(false)}>
              <ListItem disablePadding><ListItemButton><ListItemIcon><EditOutlinedIcon style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>Feedback</ListItemButton></ListItem>
            </NextLink>
            <NextLink href='/admin/likes' onClick={() => setDrawerOpen(false)}>
              <ListItem disablePadding><ListItemButton><ListItemIcon><ThumbUpAltIcon style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>Likes</ListItemButton></ListItem>
            </NextLink>
            <NextLink href='/admin/shares' onClick={() => setDrawerOpen(false)}>
              <ListItem disablePadding><ListItemButton><ListItemIcon><SendIcon style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>Shares</ListItemButton></ListItem>
            </NextLink>
            <NextLink href='/admin/referrer-analysis' onClick={() => setDrawerOpen(false)}>
              <ListItem disablePadding><ListItemButton><ListItemIcon><AssessmentOutlinedIcon style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>Referrer Analysis</ListItemButton></ListItem>
            </NextLink>
            <NextLink href='/admin/cohort-analysis' onClick={() => setDrawerOpen(false)}>
              <ListItem disablePadding><ListItemButton><ListItemIcon><AnalyticsIcon style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>Cohort Analysis</ListItemButton></ListItem>
            </NextLink>
            <NextLink href='/admin/suspensions' onClick={() => setDrawerOpen(false)}>
              <ListItem disablePadding><ListItemButton><ListItemIcon><FaceRetouchingOffIcon style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>Suspensions</ListItemButton></ListItem>
            </NextLink>
            <NextLink href='/admin/traffic' onClick={() => setDrawerOpen(false)}>
              <ListItem disablePadding><ListItemButton><ListItemIcon><ShowChartIcon style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>Traffic</ListItemButton></ListItem>
            </NextLink>
          </>)}
          <ListItem disablePadding>
            <ListItemButton onClick={() => { setDrawerOpen(false); setDialogOpen(true) }}>
              <ListItemIcon><TranslateIcon style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>
              {t('choose-language')}
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={toggleNightMode}>
              <ListItemIcon>
                {selectedTheme === 'dark'
                  ? <LightModeOutlinedIcon style={{ color: 'var(--primary-font-rgb)' }} />
                  : <BedtimeOutlinedIcon style={{ color: 'var(--primary-font-rgb)' }} />
                }
              </ListItemIcon>
              {selectedTheme === 'dark' ? 'Light mode' : 'Dark mode'}
            </ListItemButton>
          </ListItem>
          {user === null && (
            <NextLink href={`/v1/users/login?dto=${dtoString}`} onClick={() => setDrawerOpen(false)}>
              <ListItem disablePadding>
                <ListItemButton>
                  <ListItemIcon><KeyIcon style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>
                  {t('log-in')}
                </ListItemButton>
              </ListItem>
            </NextLink>
          )}
          {user === null && (
            <NextLink href='/v1/users/signup' onClick={() => setDrawerOpen(false)}>
              <ListItem disablePadding>
                <ListItemButton>
                  <ListItemIcon><ManageAccountsOutlinedIcon style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>
                  {t('sign-up')}
                </ListItemButton>
              </ListItem>
            </NextLink>
          )}
          {user && (
            <NextLink href='/v1/logout' onClick={() => setDrawerOpen(false)}>
              <ListItem disablePadding>
                <ListItemButton>
                  <ListItemIcon><Logout style={{ color: 'var(--primary-font-rgb)' }} /></ListItemIcon>
                  {t('logout')}
                </ListItemButton>
              </ListItem>
            </NextLink>
          )}
        </List>
      </Drawer>

      <Dialog
        onClose={() => {
          setDialogOpen(false)
        }}
        open={dialogOpen}
      >
        <DialogTitle>{t('choose-language')}</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setDialogOpen(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme: Theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <div className={styles.languageMenu}>
            <LanguageMenu
              title={t('continents.global')}
              languageItems={[
                <LanguageItem
                  key={'en'}
                  short={'en'}
                  language={t('global-languages.en')}
                />,
                <LanguageItem
                  key={'es-MX'}
                  short={'es'}
                  language={t('latin-america-languages.es-MX')}
                />
              ]}
            />
            <LanguageMenu
              title={t('continents.asia')}
              languageItems={[
                <LanguageItem
                  key={'vi-VN'}
                  short={'vi'}
                  language={t('asia-languages.vi-VS')}
                />,
                <LanguageItem
                  key={'my-MM'}
                  short={'my'}
                  language={t('asia-languages.my-MM')}
                />
              ]}
            />
            {/* <LanguageMenu
              title={t('continents.asia')}
              languageItems={[
                <LanguageItem
                  key={'en-HK'}
                  short={'en'}
                  language={t('asia-languages.en-HK')}
                />,
                <LanguageItem
                  key={'en-IN'}
                  short={'en'}
                  language={t('asia-languages.en-IN')}
                />,
                <LanguageItem
                  key={'en-MY'}
                  short={'en'}
                  language={t('asia-languages.en-MY')}
                />,
                <LanguageItem
                  key={'en-PH'}
                  short={'en'}
                  language={t('asia-languages.en-PH')}
                />,
                <LanguageItem
                  key={'en-SG'}
                  short={'en'}
                  language={t('asia-languages.en-SG')}
                />
              ]}
            />
            <LanguageMenu
              title={t('continents.europe')}
              languageItems={[
                <LanguageItem
                  key={'en-GB'}
                  short={'en'}
                  language={t('europe-languages.en-GB')}
                />,
                <LanguageItem
                  key={'es-ES'}
                  short={'es'}
                  language={t('europe-languages.es-ES')}
                />,
                <LanguageItem
                  key={'en-IE'}
                  short={'en'}
                  language={t('europe-languages.en-IE')}
                />
              ]}
            />
            <LanguageMenu
              title={t('continents.oceania')}
              languageItems={[
                <LanguageItem
                  key={'en-AU'}
                  short={'en'}
                  language={t('oceania-languages.en-AU')}
                />
              ]}
            />
            <LanguageMenu
              title={t('continents.latin-america')}
              languageItems={[
                <LanguageItem
                  key={'es-MX'}
                  short={'es'}
                  language={t('latin-america-languages.es-MX')}
                />
              ]}
            />
            <LanguageMenu
              title={t('continents.middle-east-north-africa')}
              languageItems={[
                <LanguageItem
                  key={'en-EG'}
                  short={'en'}
                  language={t('middle-east-north-africa-languages.en-EG')}
                />
              ]}
            />
            <LanguageMenu
              title={t('continents.africa')}
              languageItems={[
                <LanguageItem
                  key={'en-ZA'}
                  short={'en'}
                  language={t('africa-languages.en-ZA')}
                />
              ]}
            /> */}
          </div>
        </DialogContent>
      </Dialog>
    </ThemeClient>
  )
}

export default PublisherHeader
